import { useCallback, useMemo, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";
import validationSchema from "./index.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import LoadingButton from "components/shared/LoadingButton";
import { dateFormat } from "helpers/common";
import close from "../../../Assets/images/cross_icon.png";
import styles from "./index.module.scss";
import TableLayoutV2New from "components/TableLayoutV2 New";
import { useGetTaskPlanTemplatesLibraryQuery, useGetTaskPlanTemplatesQuery } from "store/services/V2/TaskPlanTemplate";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { taskPlanTemplateLibraryFilter } from 'store/slices/filter';
import { SearchIcon } from "Assets/icons";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { usePersistentState } from "hooks/usePersistentState";
import { CstmPagination } from "components/Common/Common";

const TaskPlanTemplatesPopup = ({
  showPopup,
  setShowPopup,
  closePopup,
  data,
  onSelectTemplate
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const filter = useSelector(state => state?.filter?.taskPlanTemplateLibraryFilter);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentPage, setCurrentPage] = usePersistentState("school:page", 1);
  const [pageSize, setPageSize] = useState(10);

  const { data: templateData, isLoading, isError, isFetching } = useGetTaskPlanTemplatesQuery(
    { page: currentPage, limit: pageSize, ...filter }
  );
  const templateLibrary = templateData?.records;
  const totalRecords = templateData?.total_records || 0;
  const totalPages = Math.ceil(totalRecords / pageSize); 

  const editMode = !!data?.id;
  const initialValues = useMemo(() => {
    return {
      title: data?.title || "",
    };
  }, [data]);

  const handlePageChange = (page) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  };


  const handleSubmit = (values, { setSubmitting }) => {
    onSelectTemplate?.(selectedTemplate)
    setSubmitting(false);
    setShowPopup(false)
  };

  const handleFilterChange = (key) => (value) => {
    dispatch(taskPlanTemplateLibraryFilter({ ...filter, [key]: value }))
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSearchChange = useCallback(debounce((value) => {
    handleFilterChange("search")(value);
  }, 500), [handleFilterChange, 500]);

  const handleGetRowColor = (item) => {
    if (item?.status === "discharged") return "rowYellow";
    if (item?.status === "inactive") return "rowGray";
  };

  const column = [
    {
      head: "",
      accessor: "",
      isComponent: true,
      width: 50,
      component: (item) => (
        <div
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="radio"
            name="templateSelect"
            checked={selectedTemplate?.id === item?.id}
            onChange={() => setSelectedTemplate(item)}
            style={{
              accentColor: "#071E3E",
              cursor: "pointer",
              width: "16px",
              height: "16px"
            }}
          />
        </div>
      ),
    },
    {
      head: "TASK",
      accessor: "task_title",
      isComponent: true,
      width: "100%",
      sorter: true,
      
    },
    {
      head: "DATE CREATED",
      accessor: "createdAt",
      isComponent: true,
      sorter: true,
      component: (item) => (
        <div className="d-flex justify-content-end">
          {dateFormat(item?.createdAt, "MMM DD, YYYY")}
        </div>
      ),
    },
    {
      head: "STRATEGY",
      accessor: "strategy",
      headAlign: "right",
      isComponent: true,
      sorter: true,
      width: 110,
      component: (item) => (
        <div className="d-flex justify-content-end">
          {item?.strategy?.length || 0}
        </div>
      ),
    },
  ];

  return (
    <Modal
      show={showPopup}
      className={`${styles.AddNewGoalPop} modal-v2 AddNewGoalPop`}
      onHide={closePopup}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Body className={`${styles.modalBody} position-relative rounded`}>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <div className="reports-v2">
                <div
                  className="section-title d-flex align-items-center justify-content-between"
                  style={{ borderColor: "#315D99" }}
                >
                  <h6
                    className="mb-0 section-title border-bottom-0"
                    style={{ color: "#315D99" }}
                  >
                    {editMode
                      ? "EDIT TEMPLATE"
                      : "CHOOSE A TASK PLAN TEMPLATE"}
                  </h6>

                  <img
                    onClick={closePopup}
                    width={16}
                    height={16}
                    src={close}
                    alt="close"
                    style={{ cursor: "pointer" }}
                  />
                </div>

                <Row>
                  <Col md="9" className="my-2 name-input-v2">
                    <div className="d-flex align-items-center gap-3 search-input-filter-v2 readonly-box">
                      <input
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="border-0 shadow-none h-auto w-100"
                        placeholder="Type to search for a template"
                        defaultValue={filter?.search}
                        style={{ outline: "none" }}
                      />
                      <SearchIcon />
                    </div>
                  </Col>

                  <Col md="12" className="my-2">
                    {(isFetching) && <LoadingOverlay />}
                    <TableLayoutV2New
                      data={templateLibrary}
                      column={column}
                      height={`calc(100vh - 420px)`}
                      filter={filter}
                      handleSort={handleFilterChange("sort")}
                      handleGetRowColor={handleGetRowColor}
                    />
                    <CstmPagination
                      totalPages={totalPages}
                      className={'paginationV2'}
                      activePage={currentPage}
                      handlePageChange={handlePageChange}
                    />
                  </Col>
                </Row>
              </div>

              <div className="mt-3 text-end">
                <LoadingButton
                  type="submit"
                  className="blue-btn"
                  isLoading={isSubmitting}
                  disabled={!selectedTemplate}
                >
                  Choose Template
                </LoadingButton>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default TaskPlanTemplatesPopup;